import type { BookmarkChange } from '@bsync/shared';
import { combineChanges } from './merge';
import type { KeyValueStore } from './storage';

/**
 * Durable queue of changes not yet acknowledged by the server. Changes to the
 * same node coalesce (newest clock per field group wins), so an offline
 * session of any length produces at most one pending change per node.
 */
export class Outbox {
  private entries = new Map<string, BookmarkChange>();

  constructor(
    private readonly store: KeyValueStore,
    private readonly key = 'outbox',
  ) {}

  async load(): Promise<void> {
    const saved = (await this.store.get<BookmarkChange[]>(this.key)) ?? [];
    this.entries = new Map(saved.map((c) => [c.id, c]));
  }

  get size(): number {
    return this.entries.size;
  }

  get(id: string): BookmarkChange | undefined {
    return this.entries.get(id);
  }

  async stage(changes: BookmarkChange[]): Promise<void> {
    if (!changes.length) return;
    for (const c of changes) {
      const prev = this.entries.get(c.id);
      // Re-insert at the end so a node's latest change is sent after any
      // parent it now depends on.
      this.entries.delete(c.id);
      this.entries.set(c.id, prev ? combineChanges(prev, c) : c);
    }
    await this.persist();
  }

  /** Pending changes, ordered so that newly created parents precede their children. */
  pending(): BookmarkChange[] {
    const list = [...this.entries.values()];
    const creates = new Set(list.filter((c) => c.kind).map((c) => c.id));
    const emitted = new Set<string>();
    const out: BookmarkChange[] = [];
    const visiting = new Set<string>();
    const visit = (c: BookmarkChange) => {
      if (emitted.has(c.id) || visiting.has(c.id)) return;
      visiting.add(c.id);
      const parent = c.location?.parentId;
      if (parent && creates.has(parent)) {
        const pc = this.entries.get(parent);
        if (pc) visit(pc);
      }
      visiting.delete(c.id);
      emitted.add(c.id);
      out.push(c);
    };
    for (const c of list) visit(c);
    return out;
  }

  /** Drop the parts of `sent` that are still exactly what is queued. */
  async acknowledge(sent: BookmarkChange[]): Promise<void> {
    let dirty = false;
    for (const s of sent) {
      const cur = this.entries.get(s.id);
      if (!cur) continue;
      const next: BookmarkChange = { id: cur.id, kind: cur.kind };
      if (cur.content && cur.content.hlc !== s.content?.hlc) next.content = cur.content;
      if (cur.location && cur.location.hlc !== s.location?.hlc) next.location = cur.location;
      if (cur.deletion && cur.deletion.hlc !== s.deletion?.hlc) next.deletion = cur.deletion;
      if (next.content || next.location || next.deletion) this.entries.set(s.id, next);
      else this.entries.delete(s.id);
      dirty = true;
    }
    if (dirty) await this.persist();
  }

  async clear(): Promise<void> {
    this.entries.clear();
    await this.store.remove(this.key);
  }

  private persist() {
    return this.store.set(this.key, [...this.entries.values()]);
  }
}
